import MoneyTransfer from '@advisor/models/store/moneyTransfer';
// eslint-disable-next-line import/no-cycle
import MoveMoneyApi from '@advisor/services/MoveMoney';
import RwCustodianService from '@advisor/rwSdk/CustodianService';
import RwInvestmentService from '@advisor/rwSdk/InvestmentService';
import exceptionHandler from '@advisor/util/exceptionHandler';
import {formatCurrency} from '@principal/text-formatters';
import {NEW_BANK_ACCOUNT} from '@advisor/util/constants';
import {addRiaComponentTo} from '@advisor/util/store/paramInjects';
import RwApexService from '@advisor/rwSdk/ApexClientAccountOpeningService';
import OrchestrateService from '@advisor/rwSdk/OrchestrateService';

// initial state
export const stateObj = () => new MoneyTransfer();
const getters = {
  clientName: (state) =>
    state.transactionDetails ? `${state.transactionDetails.firstName} ${state.transactionDetails.lastName}` : '',
  amount: (state) => (state.transactionDetails ? formatCurrency(state.transactionDetails.transferAmount) : ''),
  brokerageAccount: (state) =>
    state.transactionDetails
      ? `${state.transactionDetails.accountTypeName} (${state.transactionDetails.accountNumber})`
      : '',
  bankAccount: (state) =>
    state.transactionDetails
      ? `${state.transactionDetails.bankName} ${state.transactionDetails.bankAccountType} (xxxxx${state.transactionDetails.lastFourBankAccountNumber})`
      : '',
  transactionTitle: (state) => {
    if (state.transactionDetails && state.transactionDetails.bankAccountId === NEW_BANK_ACCOUNT.ID) {
      return 'Transfer Request Sent!';
    }

    return 'Transfer is in progress!';
  },
  brokerageAccountAccumulatedAum: (state) => {
    const {brokerageAccounts} = state;

    return brokerageAccounts
      .filter(({isAccountInLiquidationProcess}) => !isAccountInLiquidationProcess)
      .reduce((acc, {aum}) => acc + parseFloat(aum ?? 0), 0);
  },
};
// actions
const actions = {
  // eslint-disable-next-line require-await
  async setActiveTab({commit}, payload) {
    commit('SET_ACTIVE_TAB', payload.activeTab);
  },

  async getBrokerageAccounts({commit, rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);

      const response = await RwCustodianService.getCustodianAccounts(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_BROKERAGE_ACCOUNTS', response);

      return response;
    } catch (error) {
      return exceptionHandler.handleGenericException('moneyTransfer.getBrokerageAccounts', error);
    }
  },

  async getApexLabels({commit, rootState}) {
    try {
      const fullPayload = addRiaComponentTo(rootState.settings.default);

      const response = await RwApexService.getApexLabels(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_APEX_LABELS', response);
    } catch (error) {
      exceptionHandler.handleGenericException('moneyTransfer.getApexLabels', error);
    }
  },
  async getTransferTypes({commit, rootState}) {
    try {
      const fullPayload = addRiaComponentTo(rootState.settings.default);

      const response = await RwCustodianService.getTransferTypes(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_TRANSFER_TYPES', response);
    } catch (error) {
      exceptionHandler.handleGenericException('moneyTransfer.getTransferTypes', error);
    }
  },
  async getTransferStatus({commit, rootState}) {
    try {
      const fullPayload = addRiaComponentTo(rootState.settings.default);

      const response = await RwCustodianService.getTransferStatus(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_TRANSFER_STATUS', response);
    } catch (error) {
      exceptionHandler.handleGenericException('moneyTransfer.getTransferStatus', error);
    }
  },

  async getBankAccounts({commit, rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);

      const bankAccounts = await RwCustodianService.getAllBankAccountsForClUser(fullPayload).catch((error) => {
        throw error;
      });

      bankAccounts.push({
        id: NEW_BANK_ACCOUNT.ID,
        bankName: NEW_BANK_ACCOUNT.NAME,
      });
      commit('SET_BANK_ACCOUNTS', bankAccounts);
    } catch (error) {
      exceptionHandler.handleGenericException('moneyTransfer.getBankAccounts', error);
    }
  },
  resetBankAccounts({commit}) {
    commit('SET_BANK_ACCOUNTS', []);
  },
  async getAccountLimitation({rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);
      const response = await RwCustodianService.getAccountTypeContributionLimit(fullPayload).catch((error) => {
        throw error;
      });

      return response;
    } catch (error) {
      exceptionHandler.handleGenericException('moneyTransfer.getAccountLimitation', error);

      return false;
    }
  },
  async iraConstraintsINCOMING({commit, rootState}, payload) {
    try {
      const accountNumber = {accountNumber: payload.accountNum};
      const fullPayload = addRiaComponentTo(accountNumber, rootState.settings.default);
      const response = await RwInvestmentService.getIraConstraintsINCOMING(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_IRA_CONSTRAINTS', response);
    } catch (error) {
      exceptionHandler.handleGenericException('moneyTransfer.iraConstraintsINCOMING', error);
    }
  },
  async iraConstraintsOutgoing({commit, rootState}, payload) {
    try {
      const accountNumber = {accountNumber: payload.accountNum};
      const fullPayload = addRiaComponentTo(accountNumber, rootState.settings.default);
      const response = await RwInvestmentService.getIraConstraintsOUTGOING(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_IRA_CONSTRAINTS', response);
    } catch (error) {
      exceptionHandler.handleGenericException('moneyTransfer.iraConstraintsOutgoing', error);
    }
  },
  // eslint-disable-next-line require-await
  async setUpReview({commit}, payload) {
    commit('SETUP_REVIEW', payload);
  },
  async sendTransferMoneyEmail({rootState}, payload) {
    try {
      const rwSdkPayload = {
        clUserId: payload.clientId,
        standardEmailParameters: payload.payload,
        ...payload,
      };

      const fullPayload = addRiaComponentTo(rwSdkPayload, rootState.settings.default);

      await OrchestrateService.sendEmailToClient(fullPayload).catch((error) => {
        throw error;
      });
    } catch (error) {
      exceptionHandler.handleGenericException('moneyTransfer.sendTransferMoneyEmail', error);
    }
  },
  async fetchTransactionDetails({commit}, payload) {
    try {
      const response = await RwCustodianService.getTransferMoneyRequests(payload).catch((error) => {
        throw error;
      });
      const {data} = response;

      commit('SET_TRANSACTION_DETAILS', data[0]);
    } catch (error) {
      exceptionHandler.handleGenericException('moneyTransfer.fetchTransactionDetails', error);
    }
  },
  async getTransferFrequencyList({commit, rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);

      const response = await RwCustodianService.getCustodianTransferFrequency(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_TRANSFER_FREQUENCY', response);
    } catch (error) {
      exceptionHandler.handleGenericException('moneyTransfer.getTransferFrequencyList', error);
    }
  },
  async getDistributionReasons({commit, rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);

      const response = await RwCustodianService.getIraDistributionReasons(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_DISTRIBUTION_REASON', response);
    } catch (error) {
      exceptionHandler.handleGenericException('moneyTransfer.getDistributionReasons', error);
    }
  },
  async saveTransferRequest({commit, rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);
      const sdkResponse = await RwCustodianService.transferMoneyRequest(fullPayload).catch((error) => {
        throw error;
      });

      commit('TRANSFER_DETAILS', sdkResponse);

      return sdkResponse;
    } catch (error) {
      exceptionHandler.handleGenericException('moneyTransfer.saveTransferRequest', error);

      return Promise.reject(error);
    }
  },
  async updateTransferRequest({commit, rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);
      const sdkResponse = await RwCustodianService.updateMoneyTransferRequest(fullPayload).catch((error) => {
        throw error;
      });

      commit('TRANSFER_DETAILS', sdkResponse);

      return sdkResponse;
    } catch (error) {
      exceptionHandler.handleGenericException('moneyTransfer.updateTransferRequest', error);

      return Promise.reject(error);
    }
  },
  async initialWithdrawalDate({commit}, payload) {
    try {
      const newPayload = payload ? {transferDate: payload} : {};

      const sdkResponse = await RwCustodianService.getNextBusinessDate(newPayload).catch((error) => {
        throw error;
      });

      commit('SET_WITHDRAWAL_DATE', sdkResponse);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      return exceptionHandler.handleGenericException('moneyTransfer.initialWithdrawalDate', error);
    }
  },
  async unlinkBankAccount(_, payload) {
    try {
      const {bankAccountId, custodian, brokerageAccountId} = payload;
      const requestObj = {
        bankAccountId,
        custodian,
      };

      await MoveMoneyApi.unlinkBankAccount(requestObj, brokerageAccountId);
    } catch (error) {
      exceptionHandler.handleGenericException('moneyTransfer.unlinkBankAccount', error);
    }
  },
  async fetchDailyTransferAmountLimit(_, payload) {
    try {
      const response = await RwCustodianService.dailyTransferAmountLimit(payload).catch((error) => {
        throw error;
      });

      return response;
    } catch (error) {
      exceptionHandler.handleGenericException('moneyTransfer.fetchDailyTransferAmountLimit', error);

      return Promise.reject(error);
    }
  },
  // eslint-disable-next-line require-await
  async toDisableContinue({commit}, payload) {
    commit('SET_DISABLED_CONTINUE', payload);
  },
};

// mutations
const mutations = {
  // eslint-disable-next-line no-shadow
  SET_ACTIVE_TAB(state, payload) {
    state.activeTab = payload;
  },
  SET_BROKERAGE_ACCOUNTS(state, payload) {
    state.brokerageAccounts = payload;
  },
  SET_BANK_ACCOUNTS(state, payload) {
    state.bankAccounts = payload;
  },
  SET_IRA_CONSTRAINTS(state, payload) {
    state.iraConstraints = payload;
  },
  SET_TRANSFER_FREQUENCY(state, payload) {
    state.transferFrequencies = payload;
  },
  SETUP_REVIEW(state, payload) {
    state.reviewDetails = payload;
  },
  TRANSFER_DETAILS(state, payload) {
    state.transferDetails = payload;
  },
  SET_APEX_LABELS(state, payload) {
    state.apexLabels = payload;
  },
  SET_TRANSACTION_DETAILS(state, payload) {
    state.transactionDetails = payload;
  },
  SET_WITHDRAWAL_DATE(state, payload) {
    state.firstAvailableDate = payload;
  },
  SET_TRANSFER_TYPES(state, payload) {
    state.transferTypes = payload;
  },
  SET_TRANSFER_STATUS(state, payload) {
    state.transferStatus = payload;
  },
  SET_DISABLED_CONTINUE(state, payload) {
    state.disabledContinue = payload;
  },
  SET_DISTRIBUTION_REASON(state, payload) {
    state.distributionReasonList = payload;
  },
};

export default {
  namespaced: true,
  state: stateObj,
  getters,
  actions,
  mutations,
};
